import "./index.css"
import * as Sentry from "@sentry/react"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import ReactDOM from "react-dom/client"
import { DefaultErrorPage } from "./components/error/default-error-page"
import { routeTree } from "./routeTree.gen"

const sentryAllowedEnvironments = ["production", "development"]
const environment = import.meta.env.VITE_ENVIRONMENT || "development"
if (sentryAllowedEnvironments.includes(environment)) {
  Sentry.init({
    dsn: "https://c873367b92e4b05514ecff470a190fe2@o4509367348232192.ingest.us.sentry.io/4509367349477376",
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.consoleLoggingIntegration({
        levels: ["warn", "log", "info", "error"],
      }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        autoInject: true,
        buttonLabel: "Feedback",
        submitButtonLabel: "Send Feedback",
        formTitle: "Send Feedback",
        triggerLabel: "Send Feedback",
      }),
    ],
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    tracePropagationTargets: ["localhost", /^https:\/\/openiron\.io\/api/],
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    _experiments: {
      enableLogs: true,
    },
  })
}

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => {
    return <DefaultErrorPage />
  },
  defaultErrorComponent: ({ error }) => {
    return <DefaultErrorPage error={error} />
  },
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById("root")!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>
  )
}
